import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { PROCESS_STEPS, usePageMeta } from "@/lib/site";

const UNDERSTAND = [
  "Your goals for the project",
  "The property and existing conditions",
  "The improvements you want",
  "Your budget",
  "Financing considerations, when applicable",
  "Your timeline",
  "Plans or engineering requirements",
  "Permitting requirements",
  "The final outcome you want",
];

export default function Process() {
  usePageMeta(
    "Our Process — From Consultation to Final Walkthrough",
    "How Black Sky Builders guides Utah construction projects: consultation, evaluation, scope and planning, financing coordination, proposal, permits, construction, and final walkthrough."
  );

  return (
    <div data-testid="process-page">
      <PageHero
        eyebrow="Our Process"
        lines={["From First Call", "To Final Walkthrough"]}
        copy="A clear, organized path for your project — without pretending every build follows exactly the same steps."
        image={IMG.blueprint}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <div className="grid gap-16 lg:grid-cols-[1fr_380px]">
          <div>
            <Chapter index="01" eyebrow="The Path" title="How a project moves forward" />
            <div className="relative space-y-10 border-l border-line pl-10 sm:pl-14" data-testid="process-timeline">
              {PROCESS_STEPS.map((step, i) => (
                <Reveal key={step.n} delay={i * 0.05}>
                  <div className="relative">
                    <span className="absolute -left-[52px] top-0 flex h-8 w-8 items-center justify-center border border-line bg-ink font-mono text-[10px] tracking-[0.15em] text-silver sm:-left-[70px]">
                      {step.n}
                    </span>
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight sm:text-xl">{step.t}</h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-fog">{step.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <div className="border border-line bg-coal p-6 sm:p-8" data-testid="process-understand-card">
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                  What we work to understand first
                </h2>
                <ul className="mt-6 space-y-3">
                  {UNDERSTAND.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-mist">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-silver" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 border border-line bg-ink p-6 sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">A note on flexibility</p>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  No two projects follow exactly the same path. This is the framework we work from — your project's
                  actual sequence depends on its scope, your property, and local requirements.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/contact" data-testid="process-cta-button" className="btn-primary mt-6 w-full">
                Start With a Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
